import { z } from 'zod';

import {
  OCCUPANCY_STATUSES,
  UNIT_TYPES,
  type OccupancyStatus,
  type UnitType,
} from './constants.js';

/** Hard cap so a stray spreadsheet can't create thousands of units in one request. */
export const UNIT_IMPORT_MAX_ROWS = 500;

export const UNIT_CSV_TEMPLATE = `unit_number,floor,type,occupancy,bedrooms,bathrooms,square_feet,notes,gas,electric
101,1,apartment,occupied,2,1,650,,yes,yes
102,1,apartment,vacant,1,1,,,no,yes
`;

const HEADER_ALIASES: Record<string, UnitCsvColumn> = {
  'unit number': 'unitNumber',
  unit: 'unitNumber',
  'unit name': 'unitNumber',
  floor: 'floor',
  type: 'unitType',
  'unit type': 'unitType',
  occupancy: 'occupancyStatus',
  'occupancy status': 'occupancyStatus',
  bedrooms: 'bedroomCount',
  bedroom: 'bedroomCount',
  br: 'bedroomCount',
  'bedroom count': 'bedroomCount',
  bathrooms: 'bathroomCount',
  bathroom: 'bathroomCount',
  ba: 'bathroomCount',
  'bathroom count': 'bathroomCount',
  'square feet': 'squareFeet',
  sqft: 'squareFeet',
  'sq ft': 'squareFeet',
  notes: 'notes',
  comments: 'notes',
  gas: 'gasService',
  'gas service': 'gasService',
  electric: 'electricService',
  electricity: 'electricService',
  'electric service': 'electricService',
};

type UnitCsvColumn =
  | 'unitNumber'
  | 'floor'
  | 'unitType'
  | 'occupancyStatus'
  | 'bedroomCount'
  | 'bathroomCount'
  | 'squareFeet'
  | 'notes'
  | 'gasService'
  | 'electricService';

/** A cell that was present and non-blank. Blank cells are omitted so an
 *  override does not wipe a value the spreadsheet left empty. */
export interface UnitCsvRow {
  rowNumber: number;
  unitNumber: string;
  floor?: string;
  unitType?: UnitType;
  occupancyStatus?: OccupancyStatus;
  bedroomCount?: number;
  bathroomCount?: number;
  squareFeet?: number;
  notes?: string;
  gasService?: boolean;
  electricService?: boolean;
}

export interface UnitCsvIssue {
  rowNumber: number;
  unitNumber: string | null;
  message: string;
}

export interface UnitCsvParseResult {
  rows: UnitCsvRow[];
  errors: UnitCsvIssue[];
}

const UNIT_TYPE_SET = new Set<string>(UNIT_TYPES);
const OCCUPANCY_SET = new Set<string>(OCCUPANCY_STATUSES);

const TRUE_WORDS = new Set(['yes', 'y', 'true', '1']);
const FALSE_WORDS = new Set(['no', 'n', 'false', '0']);

export const UNIT_CSV_FIELD_LABELS: Record<Exclude<UnitCsvColumn, 'unitNumber'>, string> = {
  floor: 'Floor',
  unitType: 'Type',
  occupancyStatus: 'Occupancy',
  bedroomCount: 'Bedrooms',
  bathroomCount: 'Bathrooms',
  squareFeet: 'Square feet',
  notes: 'Notes',
  gasService: 'Gas',
  electricService: 'Electric',
};

export function unitCsvFieldLabels(row: UnitCsvRow): string[] {
  const labels: string[] = [];
  for (const key of Object.keys(UNIT_CSV_FIELD_LABELS) as Array<keyof typeof UNIT_CSV_FIELD_LABELS>) {
    if (row[key] !== undefined) labels.push(UNIT_CSV_FIELD_LABELS[key]);
  }
  return labels;
}

function normHeader(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function normToken(value: string): string {
  return value.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

/** Minimal RFC-style CSV reader. Handles quoted commas, escaped quotes, and a BOM. */
export function parseCsvTable(text: string): string[][] {
  const input = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < input.length; i++) {
    const char = input[i]!;
    if (inQuotes) {
      if (char === '"') {
        if (input[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cell += char;
      }
      continue;
    }
    if (char === '"') {
      inQuotes = true;
      continue;
    }
    if (char === ',') {
      row.push(cell);
      cell = '';
      continue;
    }
    if (char === '\n') {
      row.push(cell);
      cell = '';
      if (row.some((value) => value.trim() !== '')) rows.push(row);
      row = [];
      continue;
    }
    cell += char;
  }

  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    if (row.some((value) => value.trim() !== '')) rows.push(row);
  }
  return rows;
}

export function parseUnitCsv(text: string): UnitCsvParseResult {
  const table = parseCsvTable(text);
  if (table.length === 0) {
    return {
      rows: [],
      errors: [{ rowNumber: 0, unitNumber: null, message: 'The file is empty.' }],
    };
  }

  const header = table[0]!;
  const columns: Array<UnitCsvColumn | null> = header.map((cell) => {
    const key = normHeader(cell);
    if (!key) return null;
    return HEADER_ALIASES[key] ?? null;
  });

  if (!columns.includes('unitNumber')) {
    return {
      rows: [],
      errors: [
        {
          rowNumber: 1,
          unitNumber: null,
          message: 'Add a unit_number column. Download the template if you need the expected headers.',
        },
      ],
    };
  }

  const unknownHeaders = header
    .map((cell, index) => ({ cell: cell.trim(), column: columns[index] }))
    .filter((entry) => entry.cell.length > 0 && entry.column === null)
    .map((entry) => entry.cell);
  if (unknownHeaders.length > 0) {
    return {
      rows: [],
      errors: [
        {
          rowNumber: 1,
          unitNumber: null,
          message: `Unknown column${unknownHeaders.length === 1 ? '' : 's'}: ${unknownHeaders.join(', ')}.`,
        },
      ],
    };
  }

  const dataRows = table.slice(1);
  if (dataRows.length === 0) {
    return {
      rows: [],
      errors: [{ rowNumber: 0, unitNumber: null, message: 'Add at least one unit under the header row.' }],
    };
  }
  if (dataRows.length > UNIT_IMPORT_MAX_ROWS) {
    return {
      rows: [],
      errors: [
        {
          rowNumber: 0,
          unitNumber: null,
          message: `This file has ${dataRows.length} units. Import up to ${UNIT_IMPORT_MAX_ROWS} at a time.`,
        },
      ],
    };
  }

  const rows: UnitCsvRow[] = [];
  const errors: UnitCsvIssue[] = [];
  const seen = new Set<string>();

  dataRows.forEach((cells, index) => {
    const rowNumber = index + 2;
    const raw: Partial<Record<UnitCsvColumn, string>> = {};
    columns.forEach((column, cellIndex) => {
      if (!column) return;
      const value = (cells[cellIndex] ?? '').trim();
      if (value.length > 0) raw[column] = value;
    });

    const unitNumber = raw.unitNumber?.trim() ?? '';
    if (!unitNumber) {
      errors.push({ rowNumber, unitNumber: null, message: 'Unit number is required.' });
      return;
    }
    if (unitNumber.length > 40) {
      errors.push({ rowNumber, unitNumber, message: 'Unit number must be 40 characters or fewer.' });
      return;
    }
    if (seen.has(unitNumber)) {
      errors.push({ rowNumber, unitNumber, message: `Unit ${unitNumber} appears more than once in this file.` });
      return;
    }
    seen.add(unitNumber);

    const row: UnitCsvRow = { rowNumber, unitNumber };
    const rowErrors: string[] = [];

    if (raw.floor !== undefined) {
      if (raw.floor.length > 20) rowErrors.push('Floor must be 20 characters or fewer.');
      else row.floor = raw.floor;
    }
    if (raw.unitType !== undefined) {
      const token = normToken(raw.unitType);
      if (!UNIT_TYPE_SET.has(token)) {
        rowErrors.push(`Type must be one of: ${UNIT_TYPES.join(', ')}.`);
      } else {
        row.unitType = token as UnitType;
      }
    }
    if (raw.occupancyStatus !== undefined) {
      const token = normToken(raw.occupancyStatus);
      if (!OCCUPANCY_SET.has(token)) {
        rowErrors.push(`Occupancy must be one of: ${OCCUPANCY_STATUSES.join(', ')}.`);
      } else {
        row.occupancyStatus = token as OccupancyStatus;
      }
    }
    if (raw.bedroomCount !== undefined) {
      const parsed = parseWholeNumber(raw.bedroomCount, 50);
      if (parsed === null) rowErrors.push('Bedrooms must be a whole number from 0 to 50.');
      else row.bedroomCount = parsed;
    }
    if (raw.bathroomCount !== undefined) {
      const parsed = parseBathrooms(raw.bathroomCount);
      if (parsed === null) rowErrors.push('Bathrooms must be a number from 0 to 50, such as 1 or 1.5.');
      else row.bathroomCount = parsed;
    }
    if (raw.squareFeet !== undefined) {
      const parsed = parseWholeNumber(raw.squareFeet.replace(/,/g, ''), 1_000_000);
      if (parsed === null) rowErrors.push('Square feet must be a whole number.');
      else row.squareFeet = parsed;
    }
    if (raw.notes !== undefined) {
      if (raw.notes.length > 2_000) rowErrors.push('Notes must be 2,000 characters or fewer.');
      else row.notes = raw.notes;
    }
    const gas = parseBool(raw.gasService);
    if (gas === 'invalid') rowErrors.push('Gas must be yes or no.');
    else if (typeof gas === 'boolean') row.gasService = gas;
    const electric = parseBool(raw.electricService);
    if (electric === 'invalid') rowErrors.push('Electric must be yes or no.');
    else if (typeof electric === 'boolean') row.electricService = electric;

    if (rowErrors.length > 0) {
      errors.push({ rowNumber, unitNumber, message: rowErrors.join(' ') });
      return;
    }
    rows.push(row);
  });

  return { rows, errors };
}

function parseWholeNumber(value: string, max: number): number | null {
  if (!/^\d+$/.test(value)) return null;
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed) || parsed > max) return null;
  return parsed;
}

function parseBathrooms(value: string): number | null {
  if (!/^\d+(\.\d)?$/.test(value)) return null;
  const parsed = Number(value);
  if (parsed > 50) return null;
  return parsed;
}

function parseBool(value: string | undefined): boolean | 'invalid' | undefined {
  if (value === undefined) return undefined;
  const token = value.trim().toLowerCase();
  if (TRUE_WORDS.has(token)) return true;
  if (FALSE_WORDS.has(token)) return false;
  return 'invalid';
}

export const unitImportDuplicatePolicySchema = z.enum(['override', 'ignore']);
export type UnitImportDuplicatePolicy = z.infer<typeof unitImportDuplicatePolicySchema>;

export const previewUnitImportInput = z.object({
  csv: z.string().trim().min(1, 'CSV is empty').max(1_000_000),
});
export type PreviewUnitImportInput = z.infer<typeof previewUnitImportInput>;

export const commitUnitImportInput = previewUnitImportInput.extend({
  duplicatePolicy: unitImportDuplicatePolicySchema,
});
export type CommitUnitImportInput = z.infer<typeof commitUnitImportInput>;
